import {Access} from 'gabi-api-ts/v2/group/common/group_common';
import {Group} from 'gabi-api-ts/v2/group/query/group_query';
import {GroupQueryServiceClient} from 'gabi-api-ts/v2/group/query/group_query.client';

import {usePatientDataArgs} from '@/hooks/backend/use-patient-data';
import {useBackendQuery, useBackendQueryResult} from '@/hooks/use-backend-query';
import {NoExtraProp} from '@/types/no-extra-prop';

export type usePatientGroupsArgs = usePatientDataArgs & {
    access?: Access;
};

function usePatientGroups(options: NoExtraProp<usePatientGroupsArgs>, dependencies?: unknown[]) : useBackendQueryResult<Group[]> {
    const [patientGroupIdsLoading, patientGroupIds, patientGroupIdsError, patientGroupIdsRefresh] = useBackendQuery({
        serviceClient: GroupQueryServiceClient,
        query: GroupQueryServiceClient.prototype.findAccess,
        disabled: options.disabled,
        memoize: options.memoize,
        data: {
            id: {
                id: options.patientId,
            },
            domain: 'patients',
            access: options.access || Access.READ,
        },
    }, dependencies);

    const [userGroupsLoading, userGroups, userGroupsError, userGroupsRefresh] = useBackendQuery({
        serviceClient: GroupQueryServiceClient,
        query: GroupQueryServiceClient.prototype.findAll,
        disabled: options.disabled,
        memoize: options.memoize,
        data: {},
    }, dependencies);

    const refresh = () => {
        if (patientGroupIdsRefresh) {
            patientGroupIdsRefresh();
        }
        if (userGroupsRefresh) {
            userGroupsRefresh();
        }
    };

    if (userGroupsLoading || patientGroupIdsLoading) {
        return [
            true,
            null,
            null,
            refresh,
        ];
    }
    if (userGroupsError) {
        return [
            false,
            null,
            userGroupsError,
            refresh,
        ];
    }
    if (patientGroupIdsError) {
        return [
            false,
            null,
            patientGroupIdsError,
            refresh,
        ];
    }

    const userGroupsMap: {[key:string]: Group} = userGroups!.group.reduce((acc, g) => {
        return {
            ...acc,
            [g.groupId?.id ?? '']: g,
        };
    }, {});
    const groups: Group[] = patientGroupIds!.groupId.map(groupId => {
        return (userGroupsMap[groupId.id])!;
    });

    return [
        false,
        groups,
        userGroupsError,
        refresh,
    ];
}

export { usePatientGroups };
